import type { ProfileView } from '@/api/catalog-client';

// =============================================================================
// catalog-profile-tree.ts — groupement FRONT des profils en arborescence
// familles → sous-familles → profils (DBU §2.4 l.938 : « organisation du catalogue
// en familles et sous-familles […] vue en arborescence »).
// =============================================================================
// Groupement PUR côté front à partir des attributs `famille`/`sousFamille` déjà
// portés par chaque ProfileView (aucune API dédiée). Déterministe et STABLE :
// l'ordre de PREMIÈRE APPARITION dans la liste source est préservé à chaque niveau
// (familles, sous-familles, profils). Le serveur reste l'autorité de l'ordonnancement
// (il peut renvoyer les profils dans un ordre porteur de sens, ex. alignement CIGREF) ;
// le groupement est purement VISUEL et ne réordonne pas au sein d'un groupe.
// Aucune donnée nominative (référentiel GLOBAL).

/** Libellé de repli quand `famille`/`sousFamille` est vide (jamais de nœud muet). */
export const UNCLASSIFIED_FAMILY_LABEL = 'Sans famille';
export const UNCLASSIFIED_SUBFAMILY_LABEL = 'Sans sous-famille';

/** Nœud « sous-famille » : un libellé + les profils qui lui appartiennent. */
export interface ProfileSubFamilyNode {
  readonly sousFamille: string;
  readonly profiles: readonly ProfileView[];
}

/** Nœud « famille » : un libellé + ses sous-familles + le total de profils (badge). */
export interface ProfileFamilyNode {
  readonly famille: string;
  readonly sousFamilles: readonly ProfileSubFamilyNode[];
  readonly profileCount: number;
}

/** Normalise un libellé de regroupement (trim ; vide ⇒ libellé de repli). */
function groupLabel(raw: string, fallback: string): string {
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : fallback;
}

/**
 * Groupe une liste plate de profils en arborescence famille → sous-famille → profils.
 * L'ordre de première apparition est préservé à chaque niveau (Map = ordre d'insertion) :
 * le groupement ne réordonne PAS au sein d'un groupe (autorité serveur sur l'ordre). Le
 * comptage `profileCount` alimente le badge de nœud (nombre de profils sous la famille).
 */
export function groupProfilesByFamily(
  profiles: readonly ProfileView[],
): readonly ProfileFamilyNode[] {
  const byFamily = new Map<string, Map<string, ProfileView[]>>();

  for (const profile of profiles) {
    const famille = groupLabel(profile.famille, UNCLASSIFIED_FAMILY_LABEL);
    const sousFamille = groupLabel(
      profile.sousFamille,
      UNCLASSIFIED_SUBFAMILY_LABEL,
    );
    const subMap = byFamily.get(famille) ?? new Map<string, ProfileView[]>();
    const bucket = subMap.get(sousFamille) ?? [];
    bucket.push(profile);
    subMap.set(sousFamille, bucket);
    byFamily.set(famille, subMap);
  }

  return [...byFamily.entries()].map(([famille, subMap]) => {
    const sousFamilles = [...subMap.entries()].map(([sousFamille, bucket]) => ({
      sousFamille,
      profiles: [...bucket],
    }));
    const profileCount = sousFamilles.reduce(
      (total, node) => total + node.profiles.length,
      0,
    );
    return { famille, sousFamilles, profileCount };
  });
}
